import { createApi } from '@reduxjs/toolkit/query/react';
import { dynamicBaseQuery } from '../badRequestHandler';

/* ─── Types ──────────────────────────────────────────────────────────── */

interface GetPlansParams {
  page?:        number;
  limit?:       number;
  search?:      string;
  status?:      string;
  type?:        string;
  userId?:      string;
  branchId?:    string;
  category?:    string;
  subCategory?: string;
}

interface UpdatePlanArgs {
  id:   string;
  body: Record<string, unknown>;
}

interface GetUpgradablePlansParams {
  userId:    string;
  branchId?: string;
}

interface GetAddOnPackagesParams {
  type?:         string;
  addonType?:    string;
  branchId?:     string;
  membershipId?: string;
  page?:         number;
  limit?:        number;
  search?:       string;
  status?:       string;
}

interface GetOpenAddonPlansParams {
  type?:         string;
  membershipId?: string;
  branchId?:     string;
}

/* ─── API ────────────────────────────────────────────────────────────── */

export const plans = createApi({
  reducerPath: 'plans',
  baseQuery:   dynamicBaseQuery,
  tagTypes:    ['Plans'] as const,
  endpoints:   (builder) => ({
    addPlan: builder.mutation<unknown, Record<string, unknown>>({
      query: (body) => ({ url: '/plans/add', method: 'POST', body }),
      invalidatesTags: ['Plans'],
    }),
    getPlans: builder.query<unknown, GetPlansParams>({
      query: ({ page = 1, limit = 10, search = '', status = '', type = '', userId = '', branchId = '', category = '', subCategory = '' } = {}) => {
        const params = new URLSearchParams();
        params.append('page',  String(page));
        params.append('limit', String(limit));
        if (search)                          params.append('search',      search);
        if (status      && status      !== 'all') params.append('status',      status);
        if (type         && type        !== 'all') params.append('type',        type);
        if (userId)                          params.append('userId',      userId);
        if (branchId)                        params.append('branchId',    branchId);
        if (category    && category    !== 'all') params.append('category',    category);
        if (subCategory && subCategory !== 'all') params.append('subCategory', subCategory);
        return { url: `/plans?${params.toString()}`, method: 'GET' };
      },
      providesTags: ['Plans'],
    }),
    getPlanDetail: builder.query<unknown, string>({
      query: (id) => ({ url: `/plans/${id}`, method: 'GET' }),
      providesTags: ['Plans'],
    }),
    updatePlan: builder.mutation<unknown, UpdatePlanArgs>({
      query: ({ id, body }) => ({ url: `/plans/${id}`, method: 'PUT', body }),
      invalidatesTags: ['Plans'],
    }),
    deletePlan: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/plans/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Plans'],
    }),
    togglePlanStatus: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/plans/${id}/toggle-status`, method: 'PATCH' }),
      invalidatesTags: ['Plans'],
    }),
    activatePlan: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/plans/${id}/activate`, method: 'PATCH' }),
      invalidatesTags: ['Plans'],
    }),
    deactivatePlan: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/plans/${id}/deactivate`, method: 'PATCH' }),
      invalidatesTags: ['Plans'],
    }),
    getUpgradablePlans: builder.query<unknown, GetUpgradablePlansParams>({
      query: ({ userId, branchId }) => {
        const params = new URLSearchParams();
        if (branchId) params.append('branchId', branchId);
        return { url: `/plans/upgradable/${userId}?${params.toString()}`, method: 'GET' };
      },
      providesTags: ['Plans'],
    }),
    getAddOnPackages: builder.query<unknown, GetAddOnPackagesParams>({
      query: ({ type, addonType, branchId, membershipId, page, limit, search, status }) => {
        const params = new URLSearchParams();
        if (type)         params.append('type',         type);
        if (addonType)    params.append('addonType',    addonType);
        if (branchId)     params.append('branchId',     branchId);
        if (membershipId) params.append('membershipId', membershipId);
        if (page)         params.append('page',         String(page));
        if (limit)        params.append('limit',        String(limit));
        if (search)       params.append('search',       search);
        if (status && status !== 'all') params.append('status', status);
        return { url: `/plans?${params.toString()}`, method: 'GET' };
      },
      providesTags: ['Plans'],
    }),
    getOpenAddonPlans: builder.query<unknown, GetOpenAddonPlansParams>({
      query: ({ type = 'addon', membershipId } = {}) => {
        const params = new URLSearchParams();
        if (type)         params.append('type',         type);
        if (membershipId) params.append('membershipId', membershipId);
        return { url: `/plans/open?${params.toString()}`, method: 'GET' };
      },
      providesTags: ['Plans'],
    }),
    // Renew form: all open addon plans (not just the current category) for this membership.
    getAllOpenAddonPlans: builder.query<unknown, GetOpenAddonPlansParams>({
      query: ({ type = 'addon', membershipId, branchId } = {}) => {
        const params = new URLSearchParams();
        if (type)         params.append('type',         type);
        if (membershipId) params.append('membershipId', membershipId);
        if (branchId)     params.append('branchId',     branchId);
        return { url: `/plans/open/all?${params.toString()}`, method: 'GET' };
      },
      providesTags: ['Plans'],
    }),
    getPlanCategories: builder.query<unknown, void>({
      query: () => ({ url: '/plans/categories', method: 'GET' }),
      providesTags: ['Plans'],
    }),
    getPlanSubcategories: builder.query<unknown, { category: string }>({
      query: ({ category }) => ({ url: `/plans/subcategories?category=${category}`, method: 'GET' }),
      providesTags: ['Plans'],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useAddPlanMutation,useGetPlansQuery,useGetPlanDetailQuery,useUpdatePlanMutation,useDeletePlanMutation,useTogglePlanStatusMutation,useActivatePlanMutation,useDeactivatePlanMutation,useGetUpgradablePlansQuery,useGetAddOnPackagesQuery,useGetOpenAddonPlansQuery,useGetAllOpenAddonPlansQuery,useGetPlanCategoriesQuery,useGetPlanSubcategoriesQuery } = plans